import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/content";

export default function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl"
      >
        <div className="flex items-center gap-3">
          <img src={BRAND.logo} alt="BullsEdge" className="h-11 w-11 rounded-lg object-contain" />
          <div className="leading-none">
            <p className="font-display text-2xl font-bold text-slate-900">BullsEdge CRM</p>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600">
              Counselor Login
            </p>
          </div>
        </div>

        <h1 className="mt-8 font-display text-3xl font-bold uppercase text-slate-900">
          Welcome back
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Sign in to manage leads and batches.
        </p>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
          <div className="flex min-h-[52px] items-center rounded-2xl border border-slate-200 bg-white px-4 focus-within:border-emerald-brand">
            <Mail size={18} className="mr-3 text-emerald-600" />
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="username"
              className="w-full bg-transparent text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
          <div className="flex min-h-[52px] items-center rounded-2xl border border-slate-200 bg-white px-4 focus-within:border-emerald-brand">
            <Lock size={18} className="mr-3 text-emerald-600" />
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              className="w-full bg-transparent text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-emerald-brand font-bold text-white transition-transform hover:scale-[1.01] disabled:opacity-70 glow-emerald"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Signing in…
              </>
            ) : (
              <>
                Sign In <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
